import { useState, useEffect } from 'react';
import api from '../services/api';

const RELATORIOS = [
  {
    id: 'vencimentos',
    icon: '📅',
    rotulo: 'Instrumentos a Vencer',
    desc: 'Calibrações vencendo em breve',
  },
  {
    id: 'conformidade',
    icon: '✅',
    rotulo: 'Conformidade por Setor',
    desc: 'Aprovação por setor em um período',
  },
  {
    id: 'historico',
    icon: '🔍',
    rotulo: 'Histórico por Instrumento',
    desc: 'Todas as calibrações de um instrumento',
  },
  {
    id: 'status-atual',
    icon: '📋',
    rotulo: 'Status Atual',
    desc: 'Snapshot completo para auditoria + assinatura',
  },
];

export default function ReportsPage() {
  const [relAtivo, setRelAtivo] = useState('vencimentos');
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(null);
  const [preview, setPreview] = useState(null);
  const [erro, setErro] = useState('');
  const [instrumentos, setInstrumentos] = useState([]);
  const [setores, setSetores] = useState([]);

  const [filtros, setFiltros] = useState({
    dias: '30',
    setor: '',
    de: '',
    ate: '',
    instrumentId: '',
    revisor: '',
  });

  useEffect(() => {
    api.get('/instruments').then(({ data }) => {
      const lista = data.dados || (Array.isArray(data) ? data : []);
      setInstrumentos(lista);
      const unicos = [...new Set(lista.map(i => i.sector).filter(Boolean))].sort();
      setSetores(unicos);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setPreview(null);
    setErro('');
  }, [relAtivo]);

  const buildParams = (formato) => {
    const p = { formato };
    if (relAtivo === 'vencimentos') {
      p.dias = filtros.dias;
    } else if (relAtivo === 'conformidade') {
      if (filtros.setor) p.setor = filtros.setor;
      if (filtros.de) p.de = filtros.de;
      if (filtros.ate) p.ate = filtros.ate;
    } else if (relAtivo === 'status-atual') {
      if (filtros.setor) p.setor = filtros.setor;
      if (filtros.revisor) p.revisor = filtros.revisor;
    } else {
      p.instrumentId = filtros.instrumentId;
      if (filtros.de) p.de = filtros.de;
      if (filtros.ate) p.ate = filtros.ate;
    }
    return p;
  };

  const handlePreview = async () => {
    if (relAtivo === 'historico' && !filtros.instrumentId) {
      setErro('Selecione um instrumento');
      return;
    }
    if (relAtivo === 'status-atual') {
      // preview retorna { linhas, resumo }
    }
    setCarregando(true);
    setErro('');
    setPreview(null);
    try {
      const { data } = await api.get(`/reports/${relAtivo}`, { params: buildParams('preview') });
      setPreview(data);
    } catch (e) {
      setErro(e.response?.data?.mensagem || 'Erro ao carregar dados');
    } finally {
      setCarregando(false);
    }
  };

  const handleExport = async (formato) => {
    if (relAtivo === 'historico' && !filtros.instrumentId) {
      setErro('Selecione um instrumento');
      return;
    }
    setExportando(formato);
    setErro('');
    try {
      const response = await api.get(`/reports/${relAtivo}`, {
        params: buildParams(formato),
        responseType: 'blob',
      });
      const ext = formato === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${relAtivo}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setErro('Erro ao exportar. Tente novamente.');
    } finally {
      setExportando(null);
    }
  };

  const setFiltro = (k, v) => setFiltros(prev => ({ ...prev, [k]: v }));

  return (
    <div className="flex flex-col h-full p-8">
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-slate-200">Relatórios</h1>
        <p className="text-xs text-slate-500 mt-0.5">Exporte dados em PDF ou Excel</p>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Sidebar de seleção */}
        <div className="w-52 flex-shrink-0 space-y-1">
          {RELATORIOS.map(r => (
            <button
              key={r.id}
              onClick={() => { setRelAtivo(r.id); setPreview(null); setErro(''); }}
              className={`w-full text-left px-3 py-3 rounded-lg transition-colors ${
                relAtivo === r.id
                  ? 'bg-indigo-900/40 border border-indigo-700/50 text-indigo-200'
                  : 'text-slate-400 hover:bg-[var(--surface-3)] hover:text-slate-200 border border-transparent'
              }`}
            >
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm">{r.icon}</span>
                <span className="text-sm font-medium leading-tight">{r.rotulo}</span>
              </div>
              <div className="text-[10px] text-slate-600 leading-tight pl-5">{r.desc}</div>
            </button>
          ))}
        </div>

        {/* Painel principal */}
        <div className="flex-1 bg-[var(--surface-3)] rounded-xl border border-[var(--surface-4)] flex flex-col overflow-hidden min-h-0">
          {/* Header */}
          <div className="px-5 py-3.5 border-b border-[var(--surface-4)] flex items-center justify-between flex-shrink-0">
            <span className="text-sm font-semibold text-slate-200">
              {RELATORIOS.find(r => r.id === relAtivo)?.icon}{' '}
              {RELATORIOS.find(r => r.id === relAtivo)?.rotulo}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleExport('pdf')}
                disabled={exportando !== null}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--surface-1)] hover:bg-red-950/30 border border-[var(--surface-4)] hover:border-red-800/50 text-slate-400 hover:text-red-300 rounded-lg transition-colors disabled:opacity-40"
              >
                {exportando === 'pdf' ? '⏳' : '📄'} PDF
              </button>
              <button
                onClick={() => handleExport('excel')}
                disabled={exportando !== null}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--surface-1)] hover:bg-emerald-950/30 border border-[var(--surface-4)] hover:border-emerald-800/50 text-slate-400 hover:text-emerald-300 rounded-lg transition-colors disabled:opacity-40"
              >
                {exportando === 'excel' ? '⏳' : '📊'} Excel
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="px-5 py-3.5 border-b border-[var(--surface-4)] bg-[var(--surface-1)]/20 flex-shrink-0">
            {relAtivo === 'vencimentos' && (
              <div className="flex items-center gap-5">
                <span className="text-xs text-slate-500">Janela:</span>
                {['30', '60', '90'].map(d => (
                  <label key={d} className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
                    <input
                      type="radio"
                      name="dias"
                      value={d}
                      checked={filtros.dias === d}
                      onChange={() => setFiltro('dias', d)}
                      className="accent-indigo-500"
                    />
                    {d} dias
                  </label>
                ))}
              </div>
            )}

            {relAtivo === 'conformidade' && (
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Setor</div>
                  <select
                    value={filtros.setor}
                    onChange={e => setFiltro('setor', e.target.value)}
                    className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600"
                  >
                    <option value="">Todos os setores</option>
                    {setores.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">De</div>
                  <input type="date" value={filtros.de} onChange={e => setFiltro('de', e.target.value)}
                    className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Até</div>
                  <input type="date" value={filtros.ate} onChange={e => setFiltro('ate', e.target.value)}
                    className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600" />
                </div>
              </div>
            )}

            {relAtivo === 'historico' && (
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Instrumento</div>
                  <select
                    value={filtros.instrumentId}
                    onChange={e => setFiltro('instrumentId', e.target.value)}
                    className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600 w-56"
                  >
                    <option value="">Selecionar instrumento...</option>
                    {instrumentos.map(i => (
                      <option key={i._id} value={i._id}>{i.tag} — {i.description}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">De</div>
                  <input type="date" value={filtros.de} onChange={e => setFiltro('de', e.target.value)}
                    className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Até</div>
                  <input type="date" value={filtros.ate} onChange={e => setFiltro('ate', e.target.value)}
                    className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600" />
                </div>
              </div>
            )}

            {relAtivo === 'status-atual' && (
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Setor</div>
                  <select
                    value={filtros.setor}
                    onChange={e => setFiltro('setor', e.target.value)}
                    className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600"
                  >
                    <option value="">Todos os setores</option>
                    {setores.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wider">Revisor (PDF)</div>
                  <input
                    type="text"
                    placeholder="Nome do revisor..."
                    value={filtros.revisor}
                    onChange={e => setFiltro('revisor', e.target.value)}
                    className="bg-[var(--surface-1)] border border-[var(--surface-4)] text-slate-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-600 w-48"
                  />
                </div>
              </div>
            )}

            {erro && <p className="text-xs text-red-400 mt-2">{erro}</p>}

            <button
              onClick={handlePreview}
              disabled={carregando}
              className="mt-3 px-4 py-1.5 text-xs font-semibold bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {carregando ? 'Buscando...' : '🔎 Pré-visualizar'}
            </button>
          </div>

          {/* Área de preview */}
          <div className="flex-1 overflow-auto p-5">
            {carregando && <PreviewSkeleton />}
            {!carregando && preview && (
              <PreviewTable relAtivo={relAtivo} dados={preview} />
            )}
            {!carregando && !preview && (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-600">
                <span className="text-3xl opacity-30">📋</span>
                <span className="text-sm">Configure os filtros e clique em Pré-visualizar</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Preview Tables ────────────────────────────────────────────────────────────

function PreviewTable({ relAtivo, dados }) {
  if (relAtivo === 'vencimentos') {
    return (
      <div>
        <div className="text-xs text-slate-500 mb-3">{dados.length} instrumento(s)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-600 border-b border-[var(--surface-4)]">
                <th className="text-left pb-2 pr-4 font-medium">TAG</th>
                <th className="text-left pb-2 pr-4 font-medium">Descrição</th>
                <th className="text-left pb-2 pr-4 font-medium">Setor</th>
                <th className="text-left pb-2 pr-4 font-medium">Últ. Calibração</th>
                <th className="text-left pb-2 pr-4 font-medium">Próx. Calibração</th>
                <th className="text-left pb-2 pr-4 font-medium">Dias Rest.</th>
                <th className="text-left pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {dados.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-600">Nenhum instrumento encontrado</td></tr>
              )}
              {dados.map((l, i) => (
                <tr key={i} className="border-b border-[var(--surface-4)]/40 last:border-0 hover:bg-white/[0.02]">
                  <td className="py-2 pr-4 font-mono text-indigo-300 font-medium">{l.tag}</td>
                  <td className="py-2 pr-4 text-slate-300">{l.descricao}</td>
                  <td className="py-2 pr-4 text-slate-400">{l.setor}</td>
                  <td className="py-2 pr-4 text-slate-500">{l.ultimaCalibracao}</td>
                  <td className="py-2 pr-4 text-slate-400">{l.proximaCalibracao}</td>
                  <td className={`py-2 pr-4 font-medium tabular-nums ${
                    l.diasRestantes == null ? 'text-slate-600' :
                    l.diasRestantes < 0 ? 'text-red-400' :
                    l.diasRestantes <= 30 ? 'text-amber-400' : 'text-slate-400'
                  }`}>
                    {l.diasRestantes != null
                      ? (l.diasRestantes < 0 ? `${l.diasRestantes}d` : `+${l.diasRestantes}d`)
                      : '—'}
                  </td>
                  <td className="py-2"><StatusBadge status={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (relAtivo === 'conformidade') {
    return (
      <div>
        <div className="text-xs text-slate-500 mb-3">{dados.length} setor(es)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-600 border-b border-[var(--surface-4)]">
                <th className="text-left pb-2 pr-4 font-medium">Setor</th>
                <th className="text-left pb-2 pr-4 font-medium">Total</th>
                <th className="text-left pb-2 pr-4 font-medium">Aprovado</th>
                <th className="text-left pb-2 pr-4 font-medium">Reprovado</th>
                <th className="text-left pb-2 pr-4 font-medium">c/ Restrição</th>
                <th className="text-left pb-2 pr-4 font-medium">Sem Cal.</th>
                <th className="text-left pb-2 font-medium">% Aprov.</th>
              </tr>
            </thead>
            <tbody>
              {dados.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-600">Nenhum dado encontrado</td></tr>
              )}
              {dados.map((l, i) => (
                <tr key={i} className="border-b border-[var(--surface-4)]/40 last:border-0 hover:bg-white/[0.02]">
                  <td className="py-2 pr-4 text-slate-200 font-medium">{l.setor}</td>
                  <td className="py-2 pr-4 text-slate-400 tabular-nums">{l.total}</td>
                  <td className="py-2 pr-4 text-emerald-400 tabular-nums">{l.aprovado}</td>
                  <td className="py-2 pr-4 text-red-400 tabular-nums">{l.reprovado}</td>
                  <td className="py-2 pr-4 text-amber-400 tabular-nums">{l.restricao}</td>
                  <td className="py-2 pr-4 text-slate-500 tabular-nums">{l.semCalibracao}</td>
                  <td className="py-2">
                    <span className={`font-semibold tabular-nums ${
                      l.pctAprovado >= 80 ? 'text-emerald-400' :
                      l.pctAprovado >= 50 ? 'text-amber-400' : 'text-red-400'
                    }`}>{l.pctAprovado}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (relAtivo === 'status-atual') {
    const { linhas = [], resumo = {} } = dados;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total', value: resumo.total, color: 'text-slate-300' },
            { label: 'Calibrados', value: resumo.calibrados, color: 'text-emerald-400' },
            { label: 'A Vencer', value: resumo.aVencer, color: 'text-amber-400' },
            { label: 'Vencidos', value: resumo.vencidos, color: 'text-red-400' },
            { label: 'Bloqueados', value: resumo.bloqueados, color: 'text-slate-400' },
            { label: 'Em Calibração', value: resumo.emCalibracao, color: 'text-violet-400' },
            { label: 'Conformidade', value: `${resumo.conformidade ?? 0}%`, color: resumo.conformidade >= 80 ? 'text-emerald-400' : resumo.conformidade >= 60 ? 'text-amber-400' : 'text-red-400' },
          ].map(s => (
            <div key={s.label} className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg p-3 text-center">
              <div className={`text-xl font-bold tabular-nums ${s.color}`}>{s.value ?? '—'}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-500">{linhas.length} instrumento(s)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-600 border-b border-[var(--surface-4)]">
                <th className="text-left pb-2 pr-3 font-medium">TAG</th>
                <th className="text-left pb-2 pr-3 font-medium">Descrição</th>
                <th className="text-left pb-2 pr-3 font-medium">Setor</th>
                <th className="text-left pb-2 pr-3 font-medium">Criticidade</th>
                <th className="text-left pb-2 pr-3 font-medium">Últ. Calibr.</th>
                <th className="text-left pb-2 pr-3 font-medium">Próx. Calibr.</th>
                <th className="text-left pb-2 pr-3 font-medium">Dias Rest.</th>
                <th className="text-left pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {linhas.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-slate-600">Nenhum instrumento encontrado</td></tr>
              )}
              {linhas.map((l, i) => (
                <tr key={i} className="border-b border-[var(--surface-4)]/40 last:border-0 hover:bg-white/[0.02]">
                  <td className="py-1.5 pr-3 font-mono text-indigo-300 font-medium">{l.tag}</td>
                  <td className="py-1.5 pr-3 text-slate-300 max-w-[200px] truncate">{l.descricao}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{l.setor}</td>
                  <td className={`py-1.5 pr-3 font-medium ${l.criticidade === 'Alta' ? 'text-red-400' : l.criticidade === 'Média' ? 'text-amber-400' : 'text-slate-400'}`}>{l.criticidade}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{l.ultimaCalibracao}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{l.proximaCalibracao}</td>
                  <td className={`py-1.5 pr-3 font-medium tabular-nums ${l.diasRestantes == null ? 'text-slate-600' : l.diasRestantes < 0 ? 'text-red-400' : l.diasRestantes <= 30 ? 'text-amber-400' : 'text-slate-400'}`}>
                    {l.diasRestantes != null ? (l.diasRestantes < 0 ? `${l.diasRestantes}d` : `+${l.diasRestantes}d`) : '—'}
                  </td>
                  <td className="py-1.5"><StatusBadge status={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // historico
  if (!dados || Array.isArray(dados) || !dados.calibracoes) return null;
  const { instrumento, calibracoes } = dados;
  return (
    <div>
      {instrumento && (
        <div className="bg-[var(--surface-1)] rounded-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-6 border border-[var(--surface-4)]">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">TAG</div>
            <div className="text-sm font-mono font-semibold text-indigo-300">{instrumento.tag}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Descrição</div>
            <div className="text-sm text-slate-300">{instrumento.descricao}</div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Setor</div>
            <div className="text-sm text-slate-300">{instrumento.setor}</div>
          </div>
          <div className="ml-auto text-xs text-slate-600">{calibracoes.length} registro(s)</div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-600 border-b border-[var(--surface-4)]">
              <th className="text-left pb-2 pr-4 font-medium">Data</th>
              <th className="text-left pb-2 pr-4 font-medium">Tipo</th>
              <th className="text-left pb-2 pr-4 font-medium">Resultado</th>
              <th className="text-left pb-2 pr-4 font-medium">Fornecedor</th>
              <th className="text-left pb-2 pr-4 font-medium">Nº Cert.</th>
              <th className="text-left pb-2 font-medium">Próx. Data</th>
            </tr>
          </thead>
          <tbody>
            {calibracoes.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-slate-600">Nenhuma calibração encontrada</td></tr>
            )}
            {calibracoes.map((c, i) => (
              <tr key={i} className="border-b border-[var(--surface-4)]/40 last:border-0 hover:bg-white/[0.02]">
                <td className="py-2 pr-4 text-slate-300">{c.data}</td>
                <td className="py-2 pr-4 text-slate-500">{c.tipo}</td>
                <td className={`py-2 pr-4 font-medium ${
                  c.resultado === 'Aprovado' ? 'text-emerald-400' :
                  c.resultado === 'Reprovado' ? 'text-red-400' : 'text-amber-400'
                }`}>{c.resultado}</td>
                <td className="py-2 pr-4 text-slate-400">{c.fornecedor}</td>
                <td className="py-2 pr-4 text-slate-500 font-mono">{c.numeroCertificado}</td>
                <td className="py-2 text-slate-400">{c.proximaData}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    'A Vencer':      'text-amber-400 bg-amber-950/40 border-amber-800/40',
    'Vencido':       'text-red-400 bg-red-950/40 border-red-800/40',
    'Calibrado':     'text-emerald-400 bg-emerald-950/40 border-emerald-800/40',
    'Em Calibração': 'text-purple-400 bg-purple-950/40 border-purple-800/40',
    'Ag. Laudo':     'text-blue-400 bg-blue-950/40 border-blue-800/40',
    'Não Calibrado': 'text-slate-400 bg-slate-800/30 border-slate-700/40',
  };
  const cls = cfg[status] || 'text-slate-400 bg-slate-800/30 border-slate-700/40';
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border ${cls}`}>
      {status}
    </span>
  );
}

function PreviewSkeleton() {
  return (
    <div className="space-y-2.5 animate-pulse">
      <div className="h-3 bg-[var(--surface-4)] rounded w-full" />
      {[90, 75, 85, 60, 80].map((w, i) => (
        <div key={i} className="h-3 bg-[var(--surface-3)] rounded" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}
